import React, { useEffect } from 'react';
import { DatePicker, DateRangePicker, Input } from 'freemium-ui';
import { Icons } from 'freemium-ui';
import { omit } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { blockInvalidInteger } from 'routes/utils';
import Spacer from 'shared/components/Spacer';
import { LOCAL_INPUT_DATE_TIME_FORMAT } from 'shared/constants';
import {
  AccountInfoType,
  ConditionsType,
  SegmentType,
  SurveyType,
} from 'shared/types';
import { defaultScoreGroup } from './utils';
import { FieldType } from './constants';
import { AppliedFilterType } from '../..';
import FilterDropdown from './filter-dropdown';
import FilterMultiSelect from './filter-multiselect';
import TagsFilter from './tags-filter';

type FilterItemProps = {
  item: FieldType;
  fieldValue: any;
  isRestTriggered: boolean;
  segments?: SegmentType[];
  contactLists: any;
  appliedFilters: any;
  saveFilter: (data: AppliedFilterType) => void;
  accountInfo?: AccountInfoType;
  setIsDisabled: (disabled: boolean) => void;
  ratingOptions?: ConditionsType[];
  ratingGroupOptions?: ConditionsType[];
  filterFor: 'QUEUE' | 'RESPONSE';
  surveyInfo?: SurveyType;
};

export type SelectedConditionType = {
  id?: string;
  value?: any;
  isConditional?: boolean;
  conditionType?: string;
  type?: string;
  display_id?: string;
};

const FilterItem = ({
  item,
  surveyInfo,
  accountInfo,
  fieldValue,
  isRestTriggered,
  appliedFilters,
  saveFilter,
  setIsDisabled,
  filterFor,
  ...rest
}: FilterItemProps) => {
  const { t, i18n } = useTranslation();
  const {
    id,
    type,
    name,
    placeholder,
    options,
    defaultValue,
    subComponent,
    uniqueId,
    label,
  } = item;
  const nameTranslate = i18n.exists(
    `contacts.import_contact.default_fields.contact.${label}`,
  )
    ? `${t(`contacts.import_contact.default_fields.contact.${label}`)}`
    : uniqueId
    ? name
    : `${t(name)}`;
  const [selectedCondition, setSelectedCondition] = React.useState<any>(null);
  const handleChange = (selected: SelectedConditionType) => {
    const value =
      id === 'contact_segments' || id === 'contact_lists'
        ? selected.display_id || selected.id
        : selected.value || selected;
    setSelectedCondition(value ? selected : null);
    // if (value === '') return;
    if (!subComponent || (subComponent && !selected?.isConditional)) {
      saveFilter({
        ...appliedFilters,
        localState: {
          ...appliedFilters.localState,
          [id]:
            value?.end ||
            selected?.type === 'DATERANGE' ||
            selected?.type === 'MULTISELECT'
              ? selected
              : value && type === 'DATE'
              ? moment(value)
                  .tz(accountInfo?.timezone || moment.tz.guess(), true)
                  .toISOString()
              : value,
        },
      });
    } else {
      saveFilter({
        ...appliedFilters,
        localState: {
          ...appliedFilters.localState,
          [id]: {
            key: item.key,
            condition: selected,
            value: '',
          },
        },
      });
      if (selected?.conditionType !== 'NONE') setIsDisabled(true);
    }
    setIsDisabled(false);
  };

  const handleSubCompChange = (subCompValue: any) => {
    switch (selectedCondition.conditionType) {
      case 'DROPDOWN':
        saveFilter({
          ...appliedFilters,
          localState: {
            ...appliedFilters.localState,
            [id]: {
              key: item.key,
              condition: selectedCondition,
              value: subCompValue.id,
            },
          },
        });
        setIsDisabled(false);
        break;

      case 'DATE_TIME':
        saveFilter({
          ...appliedFilters,
          localState: {
            ...appliedFilters.localState,
            [id]: {
              condition: selectedCondition,
              value:
                selectedCondition.name === 'After'
                  ? moment(subCompValue)
                      .tz(accountInfo?.timezone || moment.tz.guess(), true)
                      .endOf('day')
                      .toISOString()
                  : selectedCondition.name === 'Before'
                  ? moment(subCompValue)
                      .tz(accountInfo?.timezone || moment.tz.guess(), true)
                      .startOf('day')
                      .toISOString()
                  : moment(subCompValue)
                      .tz(accountInfo?.timezone || moment.tz.guess(), true)
                      .toISOString(),
            },
          },
        });
        setIsDisabled(false);
        break;
      case 'DATERANGE':
        saveFilter({
          ...appliedFilters,
          localState: {
            ...appliedFilters.localState,
            [id]: {
              condition: selectedCondition,
              value: {
                start: moment(subCompValue[0])
                  .tz(accountInfo?.timezone || moment.tz.guess(), true)
                  .startOf('day')
                  .toISOString(),
                end: moment(subCompValue[1])
                  .tz(accountInfo?.timezone || moment.tz.guess(), true)
                  .endOf('day')
                  .toISOString(),
              },
              type: 'range',
            },
          },
        });
        setIsDisabled(false);
        break;
      case 'NUMBER':
        saveFilter({
          ...appliedFilters,
          localState: {
            ...appliedFilters.localState,
            [id]: {
              condition: selectedCondition,
              value: subCompValue,
            },
          },
        });
        setIsDisabled(false);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setSelectedCondition(null);
    saveFilter({
      ...appliedFilters,
      localState: omit(appliedFilters.localState, [id]),
    });
  };

  const getFilterOptions = (fieldId: string) => {
    switch (fieldId) {
      case 'contact_segments':
        return rest?.segments || [];
      case 'contact_lists':
      case 'contact_filter.list_ids':
      case 'contact_response.list_ids':
        return rest?.contactLists;
      case 'ratings.prev_nps_rating_group':
      case 'ratings.prev_csat_rating_group':
      case 'ratings.prev_ces_rating_group':
      case 'ratings.nps_rating_group':
      case 'ratings.csat_rating_group':
      case 'ratings.ces_rating_group':
        return rest?.ratingGroupOptions || [];
      case 'ratings.scoring_group':
        return [...defaultScoreGroup()];
      default:
        return options;
    }
  };

  const FilterField = (type: string) => {
    switch (type) {
      case 'DROPDOWN':
      case 'CHECKBOX':
      case 'NUMBER':
        return (
          <FilterDropdown
            label={nameTranslate}
            selected={selectedCondition || defaultValue}
            options={getFilterOptions(id)}
            handleChange={handleChange}
            placeholder={`${t(placeholder)}`}
          />
        );
      case 'MULTISELECT':
        return (
          <TagsFilter
            label={nameTranslate}
            selected={selectedCondition}
            handleChange={handleChange}
            placeholder={`${t(placeholder)}`}
          />
        );
      case 'TEXT':
      case 'EMAIL':
        return (
          <Input
            className="response-filter-input"
            label={nameTranslate}
            type="text"
            name={name}
            value={selectedCondition || ''}
            placeholder={`${t('forms.placeholders.any')}`}
            onChange={(e: any) => {
              handleChange(e.target.value);
            }}
          />
        );
      case 'MULTI_SELECT':
        return (
          <FilterMultiSelect
            label={nameTranslate}
            selected={selectedCondition || []}
            options={getFilterOptions(id)}
            handleChange={handleChange}
            placeholder={`${t('forms.placeholders.any')}`}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isRestTriggered) {
      setSelectedCondition(null);
      window.localStorage.setItem(
        filterFor === 'RESPONSE' ? 'response:filter' : 'queue:filter',
        JSON.stringify({ filterParams: '', localState: {}, isApply: false }),
      );
    }
  }, [isRestTriggered]);

  useEffect(() => {
    const { localState } = appliedFilters;
    if (Object.keys(localState).length > 0) {
      if (localState[id]?.condition) {
        setSelectedCondition(localState[id].condition);
      } else {
        setSelectedCondition(localState[id]);
      }
    } else {
      setSelectedCondition(null);
    }
  }, [appliedFilters]);

  return (
    <>
      {selectedCondition &&
      (selectedCondition?.id === 'is_empty' ||
        selectedCondition?.value?.length !== 0) ? (
        <div className="clear-filter-item" onClick={() => handleClear()}>
          <Icons glyph="close" size="8" viewBox="0 0 6 6" /> {t('clear')}
        </div>
      ) : null}
      {FilterField(type)}
      {subComponent &&
      selectedCondition &&
      selectedCondition.isConditional &&
      selectedCondition.conditionType !== 'NONE' ? (
        <>
          <Spacer y={0.5} />
          <div className="secondary-filter">
            {selectedCondition.conditionType === 'DROPDOWN' ? (
              <FilterDropdown
                selected={
                  appliedFilters.localState[id]
                    ? appliedFilters.localState[id].value
                    : ''
                }
                options={
                  id === `ratings.${surveyInfo?.type?.toLowerCase()}_rating` ||
                  id ===
                    `ratings.prev_${surveyInfo?.type?.toLowerCase()}_rating`
                    ? rest?.ratingOptions || []
                    : subComponent.options
                }
                handleChange={handleSubCompChange}
                placeholder={t(subComponent.placeholder)}
              />
            ) : selectedCondition.conditionType === 'DATE_TIME' ? (
              <DatePicker
                value={
                  appliedFilters?.localState?.[id]?.value
                    ? new Date(
                        moment(appliedFilters?.localState?.[id]?.value)
                          .tz(accountInfo?.timezone || moment.tz.guess())
                          .format(LOCAL_INPUT_DATE_TIME_FORMAT),
                      )
                    : undefined
                }
                onChange={(val: any) => handleSubCompChange(val)}
                placeholder={`${t(subComponent.placeholder)}`}
              />
            ) : selectedCondition.conditionType === 'DATERANGE' ? (
              <>
                <DateRangePicker
                  onChange={(val: any) => handleSubCompChange(val)}
                  defaultValue={
                    appliedFilters?.localState?.[id]?.value
                      ? [
                          new Date(
                            moment(appliedFilters.localState?.[id].value.start)
                              .tz(accountInfo?.timezone || moment.tz.guess())
                              .format(LOCAL_INPUT_DATE_TIME_FORMAT),
                          ),
                          new Date(
                            moment(appliedFilters.localState?.[id].value.end)
                              .tz(accountInfo?.timezone || moment.tz.guess())
                              .format(LOCAL_INPUT_DATE_TIME_FORMAT),
                          ),
                        ]
                      : undefined
                  }
                />
              </>
            ) : selectedCondition.conditionType === 'NUMBER' ? (
              <Input
                className="response-filter-input"
                type="number"
                name={name}
                value={
                  appliedFilters?.localState[id]
                    ? appliedFilters?.localState[id]?.value
                    : ''
                }
                placeholder={`${t(subComponent.placeholder)}`}
                onChange={(e: any) => {
                  handleSubCompChange(e.target.value);
                }}
                onKeyDown={blockInvalidInteger}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
};

export default FilterItem;
